import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhotosManagerSimple = () => {
  const [missionsWithPhotos, setMissionsWithPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMission, setSelectedMission] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [deletingMission, setDeletingMission] = useState(null);

  // Charger toutes les missions avec photos
  const loadMissionsWithPhotos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/photos/missions-with-photos?days=60`);
      setMissionsWithPhotos(response.data.missions || []);
      setTotalPhotos(response.data.total_photos || 0);
    } catch (error) {
      console.error('Erreur chargement missions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Charger les photos d'une mission spécifique
  const loadMissionPhotos = async (missionId) => {
    setLoadingPhotos(true);
    setSelectedMission(missionId);
    try {
      const response = await axios.get(`${API}/missions/${missionId}/photos`);
      // L'API retourne directement un array
      setPhotos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement photos:', error);
      setPhotos([]);
    } finally {
      setLoadingPhotos(false);
    }
  };

  // Supprimer une photo
  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/missions/${selectedMission}/photo/${photoId}`);
      // Recharger les photos
      await loadMissionPhotos(selectedMission);
      await loadMissionsWithPhotos();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Supprimer toutes les photos d'une mission
  const handleDeleteAllPhotos = async (missionId, photoCount) => {
    setDeletingMission(missionId);
    try {
      await axios.delete(`${API}/missions/${missionId}/photos/all`);
      await loadMissionsWithPhotos();
      setSelectedMission(null);
      setPhotos([]);
      toast.success(`${photoCount} photo(s) supprimée(s)`);
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeletingMission(null);
    }
  };

  // Charger au montage
  useEffect(() => {
    loadMissionsWithPhotos();
  }, []);

  // Fermer la modal
  const closeModal = () => {
    setSelectedMission(null);
    setPhotos([]);
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              📸 Photos des missions
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Photos uploadées sur Cloudinary par les employés
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-medium">
              {totalPhotos} photo{totalPhotos > 1 ? 's' : ''}
            </span>
            <a
              href="https://console.cloudinary.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
            >
              ☁️ Cloudinary
            </a>
            <button
              onClick={loadMissionsWithPhotos}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-all"
            >
              🔄 Rafraîchir
            </button>
          </div>
        </div>

        {/* Liste des missions avec photos */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des photos...</p>
          </div>
        ) : missionsWithPhotos.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl shadow">
            <p className="text-6xl mb-4">📷</p>
            <p className="text-xl font-medium text-gray-700">Aucune photo pour le moment</p>
            <p className="text-gray-500 mt-2">Les employés peuvent uploader des photos via leurs liens personnels</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {missionsWithPhotos.map(({ mission, housing, employee, photoCount }) => (
              <div
                key={mission.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-100"
                onClick={() => loadMissionPhotos(mission.id)}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-800">
                      {housing?.pseudo || housing?.adresse || 'Mission'}
                    </h3>
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-medium">
                      {photoCount} 📷
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    📅 {mission.date_mission ? new Date(mission.date_mission).toLocaleDateString('fr-FR', {
                      weekday: 'short', day: 'numeric', month: 'short'
                    }) : '-'}
                  </p>
                  <p className="text-sm text-gray-500">
                    👤 {employee?.prenom || 'Employé'} {employee?.nom || ''}
                  </p>
                </div>
                <div className="bg-gray-50 px-4 py-2 rounded-b-xl flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    Cliquer pour voir
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAllPhotos(mission.id, photoCount);
                    }}
                    disabled={deletingMission === mission.id}
                    className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                  >
                    {deletingMission === mission.id ? '⏳' : '🗑️'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal des photos */}
        {selectedMission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Header modal */}
              <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                <h2 className="text-lg font-semibold">
                  📷 Photos de la mission
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Contenu */}
              <div className="p-4 overflow-y-auto max-h-[70vh]">
                {loadingPhotos ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement...</p>
                  </div>
                ) : photos.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Aucune photo trouvée</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {photos.map((photo) => {
                      const isCloudinary = photo.source === 'cloudinary' || photo.cloudinary_public_id;
                      const thumbnailUrl = isCloudinary ? photo.thumbnail_url : photo.photo_data;
                      const fullUrl = isCloudinary ? photo.secure_url : photo.photo_data;
                      
                      return (
                        <div key={photo.id} className="relative group">
                          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src={thumbnailUrl}
                              alt="Photo"
                              className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => window.open(fullUrl, '_blank')}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="12">Erreur</text></svg>';
                              }}
                            />
                            {isCloudinary && (
                              <span className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                ☁️
                              </span>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="mt-2 flex justify-between items-center">
                            <span className="text-xs text-gray-500">
                              {photo.uploaded_at ? new Date(photo.uploaded_at).toLocaleDateString('fr-FR') : '-'}
                            </span>
                            <div className="flex gap-2">
                              <a
                                href={fullUrl}
                                download={photo.filename}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-800"
                                title="Télécharger"
                              >
                                ⬇️
                              </a>
                              <button
                                onClick={() => handleDeletePhoto(photo.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Supprimer"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotosManagerSimple;
